import { Switch } from '@thanx/uikit/switch'
import ImageHelperText from 'components/Form/ImageHelperText'
import ImageValidationMessage from 'components/Form/ImageValidationMessage'
import React, { useEffect, useRef } from 'react'
import { Button } from 'react-bootstrap'
import { I18n } from 'react-redux-i18n'

const t = (key: string) => I18n.t(`thanx_campaigns.builder.forms.push.${key}`)

type Props = {
  imageUrl: string | undefined
  // Display name for the uploaded file. ImageUploader threads the picked
  // File.name down as `text`; on edit-existing (a stored data-URL has no
  // filename) we fall back to a generic label.
  text?: string
  title: string
  height?: number
  width?: number
  minWidth?: number
  minHeight?: number
  maxHeight?: number
  maxWidth?: number
  onClick?: () => void
  isValid?: boolean
  uploadHeight?: number
  uploadWidth?: number
  isLoading?: boolean
  setLoading?: (value: boolean) => void
}

const cellStyle: React.CSSProperties = {
  border: '1px solid #d8d8d8',
  borderRadius: 5,
}

const dashedCellStyle: React.CSSProperties = {
  border: '1px dashed #d8d8d8',
  borderRadius: 5,
}

// Push-specific image controls. Presentational child of ImageUploader (it does
// NOT own a <File> element) — it only renders the empty/filled UI and calls back
// into ImageUploader via `onClick` to open the picker, preserving the
// `notify_push_image` data-URL contract owned by ImageUploader's `setValue`.
// Distinct from the email-shared ImageControls; matches the Figma rich-push
// design (dashed "Add image" cell, then thumbnail + filename + View file/Change).
const PushImageControls: React.FC<Props> = props => {
  const {
    imageUrl,
    text,
    title,
    height,
    width,
    minWidth,
    minHeight,
    maxHeight,
    maxWidth,
    onClick,
    isValid,
    uploadHeight = 0,
    uploadWidth = 0,
    isLoading,
    setLoading,
  } = props

  const previewImage = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const img = previewImage.current
    if (!img) return
    const stopLoading = () => {
      if (setLoading) setLoading(false)
    }
    // A data-URL <img> can finish decoding before this effect runs (and on the
    // first upload the <img> only mounts after this component did), so the load
    // event may never fire for our handler — clear the spinner immediately when
    // the image is already complete. Re-run when `imageUrl` changes so a freshly
    // picked image re-attaches the handlers / re-checks completeness.
    if (img.complete) {
      stopLoading()
      return
    }
    img.onload = stopLoading
    img.onerror = stopLoading
  }, [setLoading, imageUrl])

  function onUploadImage() {
    if (onClick) onClick()
  }

  const fileName = text || t('image.uploaded_fallback')

  return (
    <div>
      <Switch
        condition={!!imageUrl}
        fallback={
          <div
            className="display-flex flex-column flex-justify-center flex-align-center cursor-pointer grey-50 padding-large"
            style={dashedCellStyle}
            onClick={onUploadImage}
          >
            <i className="fa fa-upload fa-2x margin-bottom-small" />
            <span>{t('image.add')}</span>
          </div>
        }
      >
        <div
          className="display-flex flex-align-center padding-small"
          style={cellStyle}
        >
          <div className="relative margin-right-small">
            <img
              src={imageUrl}
              alt={title}
              ref={previewImage}
              className={isLoading ? 'opacity-25' : ''}
              style={{
                width: 106,
                height: 106,
                objectFit: 'cover',
                borderRadius: 5,
              }}
            />
            <Switch condition={!!isLoading}>
              <div className="absolute left-0 top-0 full-width full-height display-flex flex-justify-center flex-align-center">
                <i className="fa fa-spin fa-circle-o-notch grey-70 fa-2x" />
              </div>
            </Switch>
          </div>
          <div className="display-flex flex-column">
            <span
              className="grey-70 margin-bottom-small"
              style={{ wordBreak: 'break-all' }}
            >
              {fileName}
            </span>
            <div>
              <a
                href={imageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="margin-right-large"
              >
                <i className="fa fa-eye" />
                &nbsp;
                {t('image.view_file')}
              </a>
              <Button
                bsStyle="link"
                className="padding-none"
                onClick={onUploadImage}
              >
                <i className="fa fa-upload" />
                &nbsp;
                {t('image.change')}
              </Button>
            </div>
          </div>
        </div>
      </Switch>
      <em className="grey-50 margin-top-tiny inline-block">
        <ImageHelperText
          width={width}
          height={height}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          minWidth={minWidth}
          minHeight={minHeight}
          prefix="Image must be "
        />
      </em>
      <ImageValidationMessage
        isValid={!!isValid}
        imageWidth={uploadWidth}
        imageHeight={uploadHeight}
        width={width}
        height={height}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        minWidth={minWidth}
        minHeight={minHeight}
      />
    </div>
  )
}

export default PushImageControls
